import httpRequest from "../../ajax";
import {
  ERROR,
  GET_JOB_SEEKER_PLANS,
  GET_MY_JOB_SEEKER_SUBSCRIPTION,
  JOB_SEEKER_SUBSCRIBE,
  JOB_SEEKER_CANCEL,
  GET_JOB_SEEKER_USAGE,
  CHECK_FEATURE_ACCESS,
  USE_CREDIT,
  GET_USER_TIER,
  SAVE_MATCH_SCORE,
  GET_MATCH_SCORE_HISTORY,
  TOGGLE_JOB_SEEKER_AUTO_RENEW,
  CANCEL_PRO_HUNTER_TRIAL,
} from "../actionTypes";
export const getJobSeekerPlans = (dispatch) =>
  httpRequest("get", "/job-seeker-subscriptions/plans")
    .then(({ data: { data } }) => {
      const action = {
        type: GET_JOB_SEEKER_PLANS,
        payload: data.plans,
      };
      dispatch(action);
      return action;
    })
    .catch(({ response }) => ({
      payload: response?.data?.message || "Failed to fetch plans",
      type: ERROR,
    }));

/**
 * Get user's current job seeker subscription.
 */
export const getMyJobSeekerSubscription = (dispatch) =>
  httpRequest("get", "/job-seeker-subscriptions/my-subscription")
    .then(({ data: { data } }) => {
      const action = {
        type: GET_MY_JOB_SEEKER_SUBSCRIPTION,
        payload: data.subscription,
      };
      dispatch(action);
      return action;
    })
    .catch(({ response }) => ({
      payload: response?.data?.message || "Failed to fetch subscription",
      type: ERROR,
    }));

/**
 * Subscribe to a job seeker plan.
 * @param {Object} payload - { plan_id, payment_method: 'wallet'|'stripe'|'jarapay' }
 */
export const subscribeToJobSeekerPlan = (dispatch, payload) =>
  httpRequest("post", "/job-seeker-subscriptions/subscribe", payload)
    .then(({ data: { data } }) => {
      const action = {
        type: JOB_SEEKER_SUBSCRIBE,
        payload: data,
      };
      dispatch(action);
      return action;
    })
    .catch(({ response }) => {
      const errorData = response?.data?.data || {};
      return {
        payload: {
          status: false,
          message: response?.data?.message || "Subscription failed",
          ...errorData
        },
        type: ERROR,
      };
    });

/**
 * Cancel current subscription.
 */
export const cancelJobSeekerSubscription = (dispatch) =>
  httpRequest("post", "/job-seeker-subscriptions/cancel")
    .then(({ data: { data } }) => {
      const action = {
        type: JOB_SEEKER_CANCEL,
        payload: data,
      };
      dispatch(action);
      return action;
    })
    .catch(({ response }) => ({
      payload: response?.data?.message || "Cancellation failed",
      type: ERROR,
    }));

/**
 * Get subscription usage history.
 */
export const getJobSeekerUsage = (dispatch, page = 1, perPage = 20) =>
  httpRequest("get", `/job-seeker-subscriptions/usage/${page}/${perPage}`)
    .then(({ data: { data } }) => {
      const action = {
        type: GET_JOB_SEEKER_USAGE,
        payload: data,
      };
      dispatch(action);
      return action;
    })
    .catch(({ response }) => ({
      payload: response?.data?.message || "Failed to fetch usage",
      type: ERROR,
    }));

/**
 * Check if user has access to a specific feature.
 * @param {string} feature - 'off_platform_scan', 'deep_peer_insight', 'ai_rewrite'
 */
export const checkFeatureAccess = (dispatch, feature) =>
  httpRequest("get", `/job-seeker-subscriptions/check-feature/${feature}`)
    .then(({ data: { data } }) => {
      const action = {
        type: CHECK_FEATURE_ACCESS,
        payload: { feature, ...data },
      };
      dispatch(action);
      return action;
    })
    .catch(({ response }) => ({
      payload: response?.data?.message || "Failed to check feature access",
      type: ERROR,
    }));

/**
 * Use a credit for a feature (Sprint Pack users).
 * @param {Object} payload - { feature, job_id?, metadata? }
 */
export const useCredit = (dispatch, payload) =>
  httpRequest("post", "/job-seeker-subscriptions/use-credit", payload)
    .then(({ data: { data } }) => {
      const action = {
        type: USE_CREDIT,
        payload: data,
      };
      dispatch(action);
      return action;
    })
    .catch(({ response }) => {
      const errorData = response?.data?.data || {};
      return {
        payload: {
          status: false,
          message: response?.data?.message || "Failed to use credit",
          ...errorData
        },
        type: ERROR,
      };
    });

/**
 * Get user's current tier.
 */
export const getUserTier = (dispatch) =>
  httpRequest("get", "/job-seeker-subscriptions/tier")
    .then(({ data: { data } }) => {
      const action = {
        type: GET_USER_TIER,
        payload: data,
      };
      dispatch(action);
      return action;
    })
    .catch(({ response }) => ({
      payload: response?.data?.message || "Failed to fetch tier",
      type: ERROR,
    }));

/**
 * Persist a Job Match Score analysis result for the current user.
 * @param {Object} payload - see backend saveMatchScore docblock
 */
export const saveMatchScore = (dispatch, payload) =>
  httpRequest("post", "/job-seeker-subscriptions/match-score/save", payload)
    .then(({ data: { data } }) => {
      const action = {
        type: SAVE_MATCH_SCORE,
        payload: data,
      };
      dispatch(action);
      return action;
    })
    .catch(({ response }) => ({
      payload: response?.data?.message || "Failed to save match score",
      type: ERROR,
    }));

/**
 * Log a match score run attempt (success or failure) for analytics.
 * Fire-and-forget — failures are logged to the console only.
 */
export const logMatchScoreRun = (payload) =>
  httpRequest("post", "/job-seeker-subscriptions/match-score/log-run", payload).catch(
    (err) => {
      console.error("Failed to log match score run:", err);
    },
  );

/**
 * Get user's paginated Job Match Score history.
 */
export const getMatchScoreHistory = (dispatch, page = 1, perPage = 20) =>
  httpRequest(
    "get",
    `/job-seeker-subscriptions/match-score/history/${page}/${perPage}`,
  )
    .then(({ data: { data } }) => {
      const action = {
        type: GET_MATCH_SCORE_HISTORY,
        payload: data,
      };
      dispatch(action);
      return action;
    })
    .catch(({ response }) => ({
      payload: response?.data?.message || "Failed to fetch match score history",
      type: ERROR,
    }));

/**
 * Purchase scan credits as a one-off payment (no subscription).
 * @param {Object} payload - { quantity?: number }
 */
export const buyScanCredits = (dispatch, payload = {}) =>
  httpRequest("post", "/job-seeker-subscriptions/buy-scan-credits", payload)
    .then(({ data: { data } }) => {
      const action = {
        type: "BUY_SCAN_CREDITS",
        payload: data,
      };
      dispatch(action);
      return action;
    })
    .catch(({ response }) => {
      const errorData = response?.data?.data || {};
      return {
        payload: {
          status: false,
          message: response?.data?.message || "Failed to purchase scan credits",
          ...errorData
        },
        type: ERROR,
      };
    });

/**
 * Get the currently active campaign (if any).
 */
export const getActiveCampaign = (dispatch) =>
  httpRequest("get", "/job-seeker-subscriptions/campaign/active")
    .then(({ data: { data } }) => {
      const action = {
        type: "GET_ACTIVE_CAMPAIGN",
        payload: data.campaign,
      };
      dispatch(action);
      return action;
    })
    .catch(({ response }) => ({
      payload: response?.data?.message || "Failed to check campaign",
      type: ERROR,
    }));

/**
 * Claim free credits from an active campaign.
 * @param {string} campaignCode
 */
export const claimCampaignCredits = (dispatch, campaignCode) =>
  httpRequest("post", "/job-seeker-subscriptions/campaign/claim", {
    campaign_code: campaignCode,
  })
    .then(({ data: { data } }) => {
      const action = {
        type: "CLAIM_CAMPAIGN_CREDITS",
        payload: data,
      };
      dispatch(action);
      return action;
    })
    .catch(({ response }) => ({
      payload: response?.data?.message || "Failed to claim campaign credits",
      type: ERROR,
    }));

export const getProHunterTrialStatus = () =>
  httpRequest("get", "/job-seeker-subscriptions/trial").then(
    ({ data: { data } }) => data
  );

export const createProHunterTrialSetupIntent = () =>
  httpRequest("post", "/job-seeker-subscriptions/trial/setup-intent").then(
    ({ data: { data } }) => data
  );

export const activateProHunterTrial = (payload = {}) =>
  httpRequest("post", "/job-seeker-subscriptions/trial/activate", payload).then(
    ({ data: { data } }) => data
  );

export const submitProHunterTrialFeedback = (payload) =>
  httpRequest("post", "/job-seeker-subscriptions/trial/feedback", payload).then(
    ({ data: { data } }) => data
  );

export const convertProHunterTrialJarapay = () =>
  httpRequest(
    "post",
    "/job-seeker-subscriptions/trial/convert-jarapay"
  ).then(({ data: { data } }) => data);

export const cancelProHunterTrial = (dispatch, payload = {}) =>
  httpRequest("post", "/job-seeker-subscriptions/trial/cancel", payload)
    .then(({ data: { data } }) => {
      const action = {
        type: CANCEL_PRO_HUNTER_TRIAL,
        payload: data,
      };
      if (dispatch) dispatch(action);
      return action;
    })
    .catch(({ response }) => ({
      payload: response?.data?.message || "Failed to cancel trial",
      type: ERROR,
    }));

export const toggleJobSeekerAutoRenew = (dispatch, payload = {}) =>
  httpRequest("post", "/job-seeker-subscriptions/toggle-auto-renew", payload)
    .then(({ data: { data } }) => {
      const innerData = data?.data || data || {};
      const action = {
        type: TOGGLE_JOB_SEEKER_AUTO_RENEW,
        payload: {
          ...innerData,
          message: data?.message || innerData?.message,
        },
      };
      if (dispatch) dispatch(action);
      return action;
    })
    .catch(({ response }) => ({
      payload: response?.data?.message || "Failed to toggle auto-renewal",
      type: ERROR,
    }));

